import { Dispatch, SetStateAction, useEffect, useState, useRef } from 'react';
import localforage from 'localforage';
import { UseStateCallback } from '../services/TypeUtils';

/**
 * This implementation uses a lock to delay the initial setStates until after
 * the first load from localstorage has happened
 */
export function useSafePersistedState<T>(
  key: string,
  defaultValue: T,
  useLocalStorage: boolean = false,
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storage = useLocalStorage ? localStorage : localforage;
  const defaultValueSet = useLocalStorage ? getItemLocalStorage(key, defaultValue) : defaultValue;
  const [state, internalSetState] = useState<T>(defaultValueSet);
  const freshnessLock = useRef<Promise<void> | undefined>(undefined);

  function setState(valueOrUpdate: SetStateAction<T>) {
    if (freshnessLock.current) {
      freshnessLock.current?.finally(() => {
        internalSetState(valueOrUpdate);
      });
    } else {
      internalSetState(valueOrUpdate);
    }
  }

  // onMount load from storage
  useEffect(() => {
    if (!useLocalStorage && storage) {
      const { promise, resolve, reject } = Promise.withResolvers<void>();
      freshnessLock.current = promise;

      const fetchPromise = storage.getItem(key) as Promise<string | null>;

      fetchPromise
        .then((value: string | null) => {
          let toSave = undefined;

          if (value === undefined || value === null) toSave = defaultValue;
          else if (value === 'true') toSave = true;
          else if (value === 'false') toSave = false;
          else if (shouldSerialize(value)) toSave = JSON.parse(value) || defaultValue;
          else toSave = value;

          internalSetState(toSave);
          resolve();
        })
        .catch((err: any) => {
          clearSession(err);
          reject();
        });
    }
  }, []);

  // saveChanges
  useEffect(() => {
    let persistValue: any = state;

    if (persistValue === null || persistValue === undefined) persistValue = defaultValue;

    if (typeof persistValue === 'object' || Array.isArray(persistValue))
      persistValue = JSON.stringify(persistValue);

    if (useLocalStorage) {
      try {
        storage.setItem(key, persistValue);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.setItem(key, persistValue)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }, [state, useLocalStorage, key, defaultValue]);

  function clearSession(err: any) {
    console.log(key, err);
    //storage.clear();
  }

  function deleteState() {
    if (useLocalStorage) {
      try {
        storage.removeItem(key);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.removeItem(key)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }

  return [state, setState, deleteState];
}

/**
 * @warning - UNSAFE
 * @deprecated -  use `useSafePersistedState` instead
 */
function usePersistedState<T>(
  key: string,
  defaultValue: T,
  useLocalStorage: boolean = false,
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storage = useLocalStorage ? localStorage : localforage;

  const defaultValueSet = useLocalStorage ? getItemLocalStorage(key, defaultValue) : defaultValue;

  const [state, setState] = useState<T>(defaultValueSet);

  useEffect(() => {
    let persistValue: any = state;

    if (persistValue === null || persistValue === undefined) persistValue = defaultValue;

    if (typeof persistValue === 'object' || Array.isArray(persistValue))
      persistValue = JSON.stringify(persistValue);

    if (useLocalStorage) {
      try {
        storage.setItem(key, persistValue);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.setItem(key, persistValue)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }, [state, useLocalStorage, key, defaultValue]);

  function clearSession(err: any) {
    console.log(key, err);
    //storage.clear();
  }

  useEffect(() => {
    if (!useLocalStorage) {
      (storage?.getItem(key) as Promise<string | null>)
        ?.then((value: string | null) => {
          let toSave = undefined;

          if (value === undefined || value === null) toSave = defaultValue;
          else if (value === 'true') toSave = true;
          else if (value === 'false') toSave = false;
          else if (shouldSerialize(value)) toSave = JSON.parse(value) || defaultValue;
          else toSave = value;

          setState(toSave);
        })
        .catch((err: any) => {
          clearSession(err);
        });
    }
  }, [key, useLocalStorage, defaultValue]);

  function deleteState() {
    if (useLocalStorage) {
      try {
        storage.removeItem(key);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.removeItem(key)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }

  return [state, setState, deleteState];
}

function getItemLocalStorage(key: string, defaultValue: any) {
  let value = localStorage.getItem(key);

  if (value === undefined || value === null) return defaultValue;

  if (value === 'true') return true;

  if (value === 'false') return false;

  try {
    if (shouldSerialize(value)) return JSON.parse(value);
  } catch (err) {
    // intentional NOOP
  }

  return value;
}

function shouldSerialize(value: string) {
  return (
    typeof value === 'string' &&
    value !== 'undefined' &&
    (value.startsWith('{') || value.startsWith('['))
  );
}

export default usePersistedState;
